<template>
  <div class="landing-page">
    <section class="hero is-dark hero-top">
      <div class="hero-body">
        <div class="columns is-vcentered">
          <div class="column is-three-fifths my-6">
            <h1 class="title is-1">
              <strong>Thread</strong> <span class="has-text-weight-light">is a Twitter app for writers</span>
              <span class="tag is-warning ml-3">BETA</span>
            </h1>
            <p class="subtitle is-4 pt-5">
              Discover a new way of working with Twitter that focuses on writing
              great tweets and threads.
            </p>
            <p class="subtitle is-4">
              Fast, smart and reliable, <strong>Thread</strong>
              gets out of your way so you can draft and publish your thoughts on Twitter effortlessly.
            </p>
            <div class="block">
              <b-button type="is-primary" tag="router-link" size="is-large" to="/app">
                <span>Try it now &ndash; no signup needed!</span>
                <b-icon icon="arrow-circle-right"></b-icon>
              </b-button>
            </div>
            <div class="block has-text-grey-light is-4">
              Thread is free, there's no sign up, and you can even try it without signing in to Twitter. Or you can
              <router-link class="has-text-link-light" :to="{ hash: 'learnmore' }">
                learn more <b-icon icon="arrow-circle-down"></b-icon>
              </router-link>
            </div>
          </div>
          <div class="column is-hidden-mobile" id="thread-demo-gif"></div>
        </div>

      </div>
    </section>


    <section class="hero is-light" id="learnmore">
      <div class="hero-body">
        <div class="container">
          <div class="columns is-variable is-8">
            <div class="column is-one-third">
              <h2 class="subtitle is-size-1">Reliable writing</h2>
              <h3 class="is-size-4 has-text-primary">You can trust <strong>Thread</strong> to get out of the
                way while giving you all the tools you expect when writing on Twitter.</h3>
            </div>
            <div class="column">
              <div class="media is-size-5">
                <figure class="media-left">
                  <b-icon icon="download" size="is-large" type="is-primary" class="mr-2"></b-icon>
                </figure>
                <div class="media-content">
                  <div class="content">
                    <strong>Never lose your work</strong>
                    &mdash; Thread saves everything you do <em>locally</em>
                    so you never have to fear tabbing away, closing your browser, or something going wrong.
                  </div>
                </div>
              </div>
              <div class="media is-size-5">
                <figure class="media-left">
                  <b-icon icon="pen-fancy" size="is-large" type="is-primary" class="mr-2"></b-icon>
                </figure>
                <div class="media-content">
                  <div class="content">
                    <strong>Write multiple threads</strong>
                    &mdash; Work on more than one draft easily, and switch between them without a hassle.
                  </div>
                </div>
              </div>

              <div class="media is-size-5">
                <figure class="media-left">
                  <b-icon icon="highlighter" size="is-large" type="is-primary" class="mr-2"></b-icon>
                </figure>
                <div class="media-content">
                  <div class="content">
                    <strong>Edit smart</strong>
                    &mdash; with features like auto-complete on emojis and usernames as you type, text flow tools when
                    your tweet gets too long, drag and drop re-order of tweets, live previews of links and
                    quotes, and lots more.
                  </div>
                </div>
              </div>

              <div class="media is-size-5">
                <figure class="media-left">
                  <b-icon icon="images" size="is-large" type="is-primary" class="mr-2"></b-icon>
                </figure>
                <div class="media-content">
                  <div class="content">
                    <strong>Easy images</strong>
                    &mdash; drag and drop from your computer, move or copy images between tweets,
                    paste from the clipboard, large image preview.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>




    <div class="section hero is-primary">
      <div class="hero-body">
        <div class="container">
          <div class="columns is-variable is-8 is-vcentered">

            <div class="column is-two-fifths" id="hotkeys-image"></div>

            <div class="column">
              <h2 class="subtitle is-size-1">Convenient control</h2>
              <h3 class="is-size-4"><strong>Hotkeys</strong> help you navigate around your
                Twitter thread with ease. You can type out an entire thread, with
                mentions, links, images and emojis, without taking your hands off the keyboard.</h3>
            </div>
          </div>
        </div>
      </div>
    </div>


    <section class="hero is-light">
      <div class="hero-body">
        <div class="container">
          <div class="columns is-variable is-8">
            <div class="column is-half">
              <h2 class="subtitle is-size-1 has-text-weight-light"><strong>Account</strong>ability</h2>
              <h3 class="is-size-4">
                Sign in with <strong>as many Twitter accounts as you like</strong> then switch between them instantly.
              </h3>
              <p class="is-size-4 mt-4">
                There's even a cool anonymous mode so you can write without fear of posting
                your thread until you're ready.
              </p>
            </div>

            <div class="column">
              <img src="/images/account-switching.gif" alt="Demo of switching between accounts" />
            </div>
          </div>
        </div>
      </div>
    </section>


    <div class="section hero hero-threads">
      <div class="hero-body">
        <div class="container">
          <div class="columns is-mobile is-centered">
            <div class="column is-four-fifths">
              <div class="box has-opacity-75 p-6">
                <h2 class="subtitle is-size-1"><span class="has-text-weight-light">Transparent</span> Timelines</h2>
                <div class="is-size-4">
                  With a simplified Recent and Timelines views, a
                  convenient sidebar for threaded views, settings to
                  customize what you see, timeline bookmarks and more,
                  Thread makes it easy to keep track of your tweets,
                  and those of others.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="section hero is-warning">
      <div class="hero-body">
        <div class="container">
          <div class="is-pulled-right">
            <b-icon type="is-black" icon="user-shield" custom-size="fa-5x" custom-class="ml-2 mb-2"/>
          </div>
          <h1 class="subtitle is-size-1 has-text-centered">Privacy as Policy</h1>
          <div class="content is-size-3">
            You might notice that there is no "cookie bar" warning you on this site. That's
            because Thread does not use cookies and does not track you.
          </div>
          <ul class="is-size-4">
            <li><strong>Respectful analytics</strong>: We use <a class="has-text-link" href="https://plausible.io/">a
              simple analytics tool</a> that uses no cookies and is fully GDPR compliant.
            </li>
            <li><strong>Privacy first</strong>: By default, all your drafts are stored on your
              browser and are only sent to our server when you post or schedule a thread. We only
              save your text if you choose to save things to the cloud. We do not track the content
              you write or post to Twitter.
            </li>
          </ul>

        </div>
      </div>
    </div>

    <div class="section hero">
      <div class="hero-body">
        <div class="container">
            <b-button expanded type="is-primary" tag="router-link" size="is-large" to="/app">
              Give it a try right now, no signup required! &raquo;
            </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "LandingPage"
}
</script>

<style lang="scss" scoped>

.is-size-1 {
  font-size: 5rem !important;
}
.title.is-1 {
  font-size: 4rem !important;
}

.hero-top {
  padding-right: 0;

  .hero-body {
    padding-top: 0;
    padding-bottom: 0;
  }

  .columns {
    margin: 0;

  }

  #thread-demo-gif {
    transition: opacity 250ms;
    opacity: 0.6;
    height: 750px;
    background: url('~@/assets/threadis-typing-demo.gif') left -80px no-repeat;
    background-size: cover;
  }

  #thread-demo-gif:hover {
    opacity: 1;

  }

}


#hotkeys-image {
  min-height: 400px;
  background: url('~@/assets/hotkeys.png') left top no-repeat;
  background-size: cover;
  margin-bottom: 2rem;
}

.hero.is-light {
  .hero-body {
    padding: 6rem 4rem;
  }
}

.hero.hero-threads {
  background-image: url('/images/thread-background-sidebar-example.jpg');
  background-size: cover;
  min-height: 200px;
}

@media only screen and (min-width: 769px) {
  .hero-top {
    .hero-body {
      padding-right: 0;
    }
  }
}

@media only screen and (min-width: 1024px) {
  .column.is-three-fifths {
    padding-left: 8rem;
    padding-right: 8rem;
  }
}

</style>
